import { XClose } from '@untitled-ui/icons-react/build/cjs';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { URLs } from 'src/constants/url';
import useGoogleMaps from 'src/hooks/useGoogleMaps';
import { logError } from 'src/services/LoggingService';
import WalToast from 'src/utils/WalToast';

interface MapBoxProps {
    address: any;
    id: string;
    onReverseAddressFunc?: any;
    draggable?: boolean;
    height?: number | string;
    className?: string;
    isDefaultSatelliteView?: boolean;
    isOpenFromQuoting?: boolean;
}

const initialMapAddressVal = {
    fullAddress: '',
    address1: '',
    city: '',
    state: '',
    country: '',
    postal: '',
    latitude: '',
    longitude: '',
    stateCode: '',
    countryCode: '',
    streetNumber: '',
};

const createStyleSwitcherControl = (t: any, isDefaultSatelliteView: boolean) => {
    let map: mapboxgl.Map;
    let container: HTMLDivElement;
    let currentStyle = isDefaultSatelliteView ? URLs.MAPBOX_SATELLITE_STYLE : URLs.MAPBOX_LIGHT_STYLE;

    const styles = [
        { value: URLs.MAPBOX_LIGHT_STYLE, label: t('Common.Roadmap') },
        {
            value: URLs.MAPBOX_SATELLITE_STYLE,
            label: t('Common.Satellite'),
        },
    ];

    const updateButtonStates = () => {
        const buttons = container.querySelectorAll('button');
        buttons.forEach((button, index) => {
            if (styles[index].value === currentStyle) {
                button.style.backgroundColor = '#fff';
                button.style.color = '#000000';
                button.style.fontWeight = '500';
            } else {
                button.style.backgroundColor = '#fff';
                button.style.color = '#565656';
                button.style.fontWeight = '400';
            }
        });
    };

    const switchStyle = (styleUrl: string) => {
        currentStyle = styleUrl;
        map.setStyle(styleUrl);
        updateButtonStates();
    };

    return {
        onAdd(mapInstance: mapboxgl.Map) {
            map = mapInstance;
            container = document.createElement('div');
            container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
            container.style.boxShadow = 'none';

            container.style.display = 'flex';

            styles.forEach((style, index) => {
                const button = document.createElement('button');
                button.textContent = style.label;
                button.style.border = 'none';
                button.style.width = 'auto';
                // Sized to sit on a 200px-tall preview map without dominating it (client, 2026-10-09).
                // These are imperative styles on a Mapbox control element, outside React's tree, so
                // they can't come from Tailwind tokens the way the rest of the UI does.
                button.style.height = '28px';
                button.style.padding = '0px 10px';
                button.style.fontSize = '12px';
                button.style.lineHeight = '28px';
                button.style.boxShadow = '0 1px 4px -1px rgba(0,0,0,0.3)';
                button.style.borderRadius = '0px';
                button.style.cursor = 'pointer';
                button.style.outline = 'none';
                button.style.transition = 'all 0.2s ease';
                button.style.fontFamily = 'Roboto, Arial, sans-serif';

                if (index < styles.length - 1) {
                    button.style.boxShadow = '0 1px 4px -1px rgba(0,0,0,0.3)';
                }

                if (index === 0) {
                    button.style.borderTopLeftRadius = '6px';
                    button.style.borderBottomLeftRadius = '6px';
                }

                if (index === styles.length - 1) {
                    button.style.borderTopRightRadius = '6px';
                    button.style.borderBottomRightRadius = '6px';
                }

                button.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    switchStyle(style.value);
                });

                button.addEventListener('mouseenter', () => {
                    button.style.backgroundColor = '#ededed';
                });

                button.addEventListener('mouseleave', () => {
                    button.style.backgroundColor = 'white';
                });

                container.appendChild(button);
            });

            updateButtonStates();

            return container;
        },

        onRemove() {
            if (container && container.parentNode) {
                container.parentNode.removeChild(container);
            }
        },
    };
};

// Satellite is the default view everywhere (client, 2026-10-09). Every quote/stepper caller
// already passed isDefaultSatelliteView explicitly; flipping the default brings the remaining
// callers (the edit-order-request map, the address modal) in line instead of making each one opt in.
const MapBox: React.FC<MapBoxProps> = ({ address, id, className, onReverseAddressFunc, draggable = true, height = 220, isDefaultSatelliteView = true, isOpenFromQuoting = false }) => {
    // Mapbox renders the map, but reverse geocoding and Street View below use google.maps, so the
    // Google script still has to be requested — it is no longer preloaded at app boot.
    useGoogleMaps();
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const streetViewContainerRef = useRef<HTMLDivElement | null>(null);
    const streetViewFullscreenContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);
    const streetViewPanoramaRef = useRef<any>(null);
    const addressRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const fullscreenPortalRef = useRef<HTMLDivElement | null>(null);
    const panoramaStateRef = useRef<any>(null); // Store panorama state for recreation
    const [isStreetView, setIsStreetView] = useState<boolean>(false);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const [isMapFullscreen, setIsMapFullscreen] = useState<boolean>(false);
    const { t } = useTranslation();

    addressRef.current = address;

    // Initialize Mapbox access token
    mapboxgl.accessToken = window?.MAPBOX_ACCESSTOKEN!;

    const reverseGeocode = async (lng: number, lat: number) => {
        try {
            if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
                console.warn(t('Common.Google_Maps_API_Not_Available'));

                if (onReverseAddressFunc) {
                    onReverseAddressFunc(initialMapAddressVal);
                }

                return;
            }

            const geocoder = new window.google.maps.Geocoder();

            geocoder.geocode({ location: { lat: lat, lng: lng } }, (results: any, status: any) => {
                if (status === 'OK') {
                    if (results[0]) {
                        const addressObject = results[0];
                        let latitudeOk = addressObject.geometry.location.lat();
                        let longitudeOk = addressObject.geometry.location.lng();
                        let city = '';
                        let state = '';
                        let postal = '';
                        let address1 = '';
                        let country = '';
                        let stateCode = '';
                        let countryCode = '';
                        let streetNumber = '';

                        addressObject.address_components.forEach((component: any) => {
                            const componentType = component.types;

                            if (componentType.includes('premise')) {
                                address1 += `${component.long_name} `;
                            } else if (componentType.includes('street_number')) {
                                streetNumber = component.long_name;
                            } else if (componentType.includes('route')) {
                                address1 += `${component.short_name} `;
                            } else if (componentType.includes('sublocality')) {
                                address1 += `${component.long_name} `;
                            } else if (componentType.includes('postal_code')) {
                                postal = `${component.long_name}${postal}`;
                            } else if (componentType.includes('postal_code_suffix')) {
                                postal = `${postal}-${component.long_name}`;
                            } else if (componentType.includes('locality')) {
                                city = component.long_name;
                            } else if (componentType.includes('administrative_area_level_1')) {
                                state = component.long_name;
                                stateCode = component.short_name;
                            } else if (componentType.includes('country')) {
                                country = component.long_name;
                                countryCode = component.short_name;
                            }
                        });

                        const fullAddress = addressObject.formatted_address;

                        if (address1 && streetNumber) {
                            address1 = address1.replace(streetNumber, '').trim();
                        }

                        const addressObj = {
                            fullAddress: fullAddress,
                            address1: address1.trim(),
                            city: city,
                            state: state,
                            country: country,
                            postal: postal,
                            latitude: latitudeOk,
                            longitude: longitudeOk,
                            stateCode: stateCode,
                            countryCode: countryCode,
                            streetNumber: streetNumber,
                        };

                        if (onReverseAddressFunc) {
                            onReverseAddressFunc(addressObj);
                        }
                    } else {
                        if (onReverseAddressFunc) {
                            onReverseAddressFunc(initialMapAddressVal);
                        }
                    }
                } else {
                    console.warn(t('Common.Google_Geocoder_Failed', { status }));

                    if (onReverseAddressFunc) {
                        onReverseAddressFunc(initialMapAddressVal);
                    }
                }
            });
        } catch (error: any) {
            await logError(error, {
                errorMessage: error?.message || 'Error reverse geocoding with Google Places',
                errorLocation: 'MapBox: reverseGeocode (Google Places)',
                storeInDatabase: true,
                logToConsole: true,
            });

            if (onReverseAddressFunc) {
                onReverseAddressFunc(initialMapAddressVal);
            }
        }
    };

    const openStreetView = () => {
        const currentAddress = addressRef.current;
        const latitude = parseFloat(currentAddress?.latitude);
        const longitude = parseFloat(currentAddress?.longitude);

        if (isNaN(latitude) || isNaN(longitude)) {
            WalToast.error(t('Common.Invalid_Coordinates_Message'));

            return;
        }

        if (!window.google || !window.google.maps || !window.google.maps.StreetViewService) {
            WalToast.error(t('Common.Google_Maps_API_Not_Loaded'));

            return;
        }

        const streetViewService = new window.google.maps.StreetViewService();
        const position = { lat: latitude, lng: longitude };

        // Check if Street View is available at this location
        streetViewService.getPanorama(
            {
                location: position,
                radius: 20,
                source: window.google.maps.StreetViewSource.OUTDOOR,
            },
            (data: any, status: any) => {
                if (status === 'OK' && data) {
                    setIsStreetView(true);

                    // Wait for next render cycle to ensure container is visible
                    setTimeout(() => {
                        if (!streetViewContainerRef.current) return;

                        if (!streetViewPanoramaRef.current) {
                            streetViewPanoramaRef.current = new window.google.maps.StreetViewPanorama(streetViewContainerRef.current, {
                                position: position,
                                pov: { heading: 0, pitch: 0 },
                                zoom: 1,
                                addressControl: true,
                                linksControl: true,
                                panControl: true,
                                enableCloseButton: false,
                                zoomControl: true,
                                fullscreenControl: false, // Disable Google's fullscreen, use our custom one
                            });
                        } else {
                            streetViewPanoramaRef.current.setPosition(position);
                            streetViewPanoramaRef.current.setVisible(true);
                        }
                    }, 100);
                } else {
                    WalToast.error(t('Common.Street_View_Not_Available'));
                }
            }
        );
    };

    const closeStreetView = () => {
        // Clean up street view panorama
        if (streetViewPanoramaRef.current) {
            streetViewPanoramaRef.current.setVisible(false);
            streetViewPanoramaRef.current = null;
        }

        // Exit fullscreen if in fullscreen mode
        if (isFullscreen) {
            setIsFullscreen(false);
        }

        setIsStreetView(false);
    };

    const toggleMapFullscreen = () => {
        if (!containerRef.current) return;

        if (!document.fullscreenElement) {
            // Enter fullscreen for map
            containerRef.current
                .requestFullscreen()
                .then(() => {
                    // Fullscreen entered successfully
                })
                .catch((err) => {
                    console.error('Error attempting to enable fullscreen:', err);
                    WalToast.error(t('Common.Unable_Enter_Fullscreen'));
                });
        } else {
            // Exit fullscreen
            document
                .exitFullscreen()
                .then(() => {
                    // Fullscreen exited successfully
                })
                .catch((err) => {
                    console.error('Error attempting to exit fullscreen:', err);
                });
        }
    };

    const toggleStreetViewFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const createFullscreenControl = () => {
        let button: HTMLButtonElement;

        const updateIcon = () => {
            if (!button) return;

            const isInFullscreen = !!document.fullscreenElement;

            if (isInFullscreen) {
                // Exit fullscreen icon
                button.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 8V3h5M17 8V3h-5M3 12v5h5M17 12v5h-5" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
                button.title = t('Common.Exit_Fullscreen');
            } else {
                // Enter fullscreen icon
                button.innerHTML = `
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M8 3H3v5M12 3h5v5M8 17H3v-5M12 17h5v-5" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
                button.title = t('Common.Enter_Fullscreen');
            }
        };

        return {
            onAdd: () => {
                const container = document.createElement('div');
                container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
                container.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.1)';

                button = document.createElement('button');
                button.className = 'mapboxgl-ctrl-icon';
                button.type = 'button';
                button.title = t('Common.Toggle_Fullscreen');
                button.setAttribute('aria-label', t('Common.Toggle_Fullscreen'));

                Object.assign(button.style, {
                    width: '28px',
                    height: '28px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    border: 'none',
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                });

                updateIcon();

                const handleClick = (e: MouseEvent) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleMapFullscreen();
                    setTimeout(updateIcon, 100);
                };

                const handleMouseEnter = () => {
                    button.style.backgroundColor = '#f0f0f0';
                };

                const handleMouseLeave = () => {
                    button.style.backgroundColor = '#fff';
                };

                // Listen for fullscreen changes to update icon
                const handleFullscreenChange = () => {
                    updateIcon();
                };

                document.addEventListener('fullscreenchange', handleFullscreenChange);

                button.addEventListener('click', handleClick);
                button.addEventListener('mouseenter', handleMouseEnter);
                button.addEventListener('mouseleave', handleMouseLeave);

                // Store the fullscreen change handler for cleanup
                (button as any)._fullscreenChangeHandler = handleFullscreenChange;

                container.appendChild(button);

                return container;
            },
            onRemove: () => {
                if (button && (button as any)._fullscreenChangeHandler) {
                    document.removeEventListener('fullscreenchange', (button as any)._fullscreenChangeHandler);
                }
            },
        };
    };

    const createStreetViewControl = () => ({
        onAdd: () => {
            const container = document.createElement('div');
            container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
            container.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.1)';

            const button = document.createElement('button');
            button.className = 'mapboxgl-ctrl-icon';
            button.type = 'button';
            button.title = t('Common.Open_Street_View');
            button.setAttribute('aria-label', t('Common.Open_Street_View'));

            Object.assign(button.style, {
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                border: 'none',
                outline: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            });

            button.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="7" r="3" fill="#666"/>
          <path d="M12 11v5m0-3l-3 1m3-1l3 1m-3 3l-3 4m3-4l3 4" stroke="#666" stroke-width="2" stroke-linecap="round"/>
        </svg>
      `;

            const handleClick = (e: MouseEvent) => {
                e.stopPropagation();
                e.preventDefault();
                openStreetView();
            };

            const handleMouseEnter = () => {
                button.style.backgroundColor = '#f0f0f0';
            };

            const handleMouseLeave = () => {
                button.style.backgroundColor = '#fff';
            };

            button.addEventListener('click', handleClick);
            button.addEventListener('mouseenter', handleMouseEnter);
            button.addEventListener('mouseleave', handleMouseLeave);

            container.appendChild(button);

            return container;
        },
        onRemove: () => {},
    });

    const initializeMap = () => {
        if (mapContainerRef.current && !mapRef.current) {
            const latitude = parseFloat(address?.latitude);
            const longitude = parseFloat(address?.longitude);

            const validCoordinates = !isNaN(latitude) && !isNaN(longitude);

            mapRef.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: isDefaultSatelliteView ? URLs.MAPBOX_SATELLITE_STYLE : URLs.MAPBOX_LIGHT_STYLE,
                center: validCoordinates ? [longitude, latitude] : [-106.346771, 56.130366],
                zoom: validCoordinates ? (isOpenFromQuoting ? 18 : 15) : 3,
                minZoom: 1,
                pitch: 0,
                bearing: 0,
                pitchWithRotate: false,
                dragRotate: false,
                touchZoomRotate: false,
                boxZoom: true,
                antialias: true,
                projection: 'mercator',
                attributionControl: false,
            });

            mapRef.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

            mapRef.current.addControl(createStyleSwitcherControl(t, isDefaultSatelliteView), 'top-left');

            // Add Fullscreen control
            mapRef.current.addControl(createFullscreenControl() as any, 'top-right');

            // Add Street View control
            mapRef.current.addControl(createStreetViewControl() as any, 'bottom-right');

            // Add marker
            if (validCoordinates) {
                markerRef.current = new mapboxgl.Marker({
                    draggable: draggable,
                    color: 'red',
                })
                    .setLngLat([longitude, latitude])
                    .addTo(mapRef.current);

                // Add drag event listener
                if (draggable && markerRef.current) {
                    markerRef.current.on('dragend', () => {
                        const lngLat = markerRef.current!.getLngLat();
                        reverseGeocode(lngLat.lng, lngLat.lat);
                    });
                }
            }

            mapRef.current.on('style.load', () => {
                if (validCoordinates && markerRef.current) {
                    markerRef.current.addTo(mapRef.current!);
                }
            });
        }
    };

    const updateMarkerPosition = () => {
        if (mapRef.current) {
            const latitude = parseFloat(address?.latitude);
            const longitude = parseFloat(address?.longitude);

            if (!isNaN(latitude) && !isNaN(longitude)) {
                const newLngLat: [number, number] = [longitude, latitude];

                if (!markerRef.current) {
                    markerRef.current = new mapboxgl.Marker({
                        draggable: draggable,
                        color: 'red',
                    })
                        .setLngLat(newLngLat)
                        .addTo(mapRef.current);

                    if (draggable && markerRef.current) {
                        markerRef.current.on('dragend', () => {
                            const lngLat = markerRef.current!.getLngLat();
                            reverseGeocode(lngLat.lng, lngLat.lat);
                        });
                    }
                } else {
                    markerRef.current.setLngLat(newLngLat);
                }

                mapRef.current.setCenter(newLngLat);
                mapRef.current.setZoom(isOpenFromQuoting ? 18 : 15);
            } else if (markerRef.current) {
                // Invalid coordinates - remove marker and reset to default view
                markerRef.current.remove();
                markerRef.current = null;
                mapRef.current.setCenter([-106.346771, 56.130366]);
                mapRef.current.setZoom(3);
            }
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            initializeMap();
        }, 500);

        return () => {
            clearTimeout(timer);

            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }

            if (streetViewPanoramaRef.current) {
                streetViewPanoramaRef.current.setVisible(false);
                streetViewPanoramaRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            updateMarkerPosition();
        }, 500);

        return () => clearTimeout(timer);
    }, [address]);

    // Track native fullscreen changes for the map
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isInFullscreen = !!document.fullscreenElement;
            setIsMapFullscreen(isInFullscreen);

            // Resize map when entering/exiting fullscreen
            if (mapRef.current) {
                setTimeout(() => {
                    mapRef.current?.resize();
                }, 100);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Handle panorama recreation when switching between fullscreen modes
    useEffect(() => {
        if (!isStreetView || !streetViewPanoramaRef.current) return;

        const targetContainer = isFullscreen ? streetViewFullscreenContainerRef.current : streetViewContainerRef.current;

        if (!targetContainer) return;

        // Save current panorama state
        try {
            const currentPosition = streetViewPanoramaRef.current.getPosition();
            const currentPov = streetViewPanoramaRef.current.getPov();
            const currentZoom = streetViewPanoramaRef.current.getZoom();

            panoramaStateRef.current = {
                position: {
                    lat: currentPosition.lat(),
                    lng: currentPosition.lng(),
                },
                pov: currentPov,
                zoom: currentZoom,
            };
        } catch (error) {
            // If we can't get the state, use address coordinates
            const currentAddress = addressRef.current;
            const latitude = parseFloat(currentAddress?.latitude);
            const longitude = parseFloat(currentAddress?.longitude);

            if (!isNaN(latitude) && !isNaN(longitude)) {
                panoramaStateRef.current = {
                    position: { lat: latitude, lng: longitude },
                    pov: { heading: 0, pitch: 0 },
                    zoom: 1,
                };
            }
        }

        // Wait a bit for the DOM to update
        setTimeout(() => {
            if (!targetContainer || !panoramaStateRef.current) return;

            // Recreate panorama in the target container
            streetViewPanoramaRef.current = new window.google.maps.StreetViewPanorama(targetContainer, {
                position: panoramaStateRef.current.position,
                pov: panoramaStateRef.current.pov,
                zoom: panoramaStateRef.current.zoom,
                addressControl: true,
                linksControl: true,
                panControl: true,
                enableCloseButton: false,
                zoomControl: true,
                fullscreenControl: false,
            });
        }, 100);
    }, [isFullscreen, isStreetView]);

    // Render street view controls
    const renderStreetViewControls = () => (
        <>
            <button
                onClick={closeStreetView}
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1001,
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#666',
                    outline: 'none',
                }}
                title={t('Common.Close_Street_View')}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                }}
            >
                <XClose />
            </button>

            {/* Fullscreen Button for Street View */}
            <button
                onClick={toggleStreetViewFullscreen}
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '60px',
                    zIndex: 1001,
                    width: '40px',
                    height: '40px',
                    backgroundColor: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                }}
                title={isFullscreen ? t('Common.Exit_Fullscreen') : t('Common.Enter_Fullscreen')}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f0f0f0';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#fff';
                }}
            >
                {isFullscreen ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M3 8V3h5M17 8V3h-5M3 12v5h5M17 12v5h-5" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M8 3H3v5M12 3h5v5M8 17H3v-5M12 17h5v-5" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </button>
        </>
    );

    return (
        <>
            <div
                ref={containerRef}
                style={{
                    position: 'relative',
                    width: '100%',
                    height: isMapFullscreen ? '100vh' : typeof height === 'string' ? height : `${height}px`,
                    backgroundColor: '#f0f0f0',
                }}
            >
                {/* Map Container */}
                <div
                    className={className}
                    id={id}
                    ref={mapContainerRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        display: isStreetView ? 'none' : 'block',
                    }}
                />

                {/* Street View Container - Regular mode (not fullscreen) */}
                {isStreetView && !isFullscreen && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            zIndex: 1000,
                            backgroundColor: '#f0f0f0',
                        }}
                    >
                        <div
                            ref={streetViewContainerRef}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        />
                        {renderStreetViewControls()}
                    </div>
                )}
            </div>

            {/* Street View Portal - Fullscreen mode via portal to bypass modal nesting */}
            {isStreetView &&
                isFullscreen &&
                ReactDOM.createPortal(
                    <div
                        ref={fullscreenPortalRef}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            zIndex: 999999,
                            backgroundColor: '#f0f0f0',
                        }}
                    >
                        <div
                            ref={streetViewFullscreenContainerRef}
                            style={{
                                width: '100%',
                                height: '100%',
                            }}
                        />
                        {renderStreetViewControls()}
                    </div>,
                    document.body
                )}
        </>
    );
};

export default MapBox;
